/**
 * Surlignage de la piste sélectionnée, en 3D : voile clair sur sa surface,
 * liseré de la couleur de sélection du HUD le long de ses bords (sur un trait
 * sombre, lisible sur la neige comme sur la forêt) et chevrons dans le sens
 * de la descente. Posé sur le relief affiché ; les tailles suivent la
 * distance de la caméra pour rester lisibles de près comme de loin.
 * Caché pendant la reprise du tracé de cette piste : l'outil la dessine alors.
 */
import * as THREE from 'three/webgpu';
import { resample, type Vec2 } from '@snowchain/shared';
import type { ClientStore } from '../state/store';
import type { RtsCamera } from './camera';
import { ui } from '../hud/ui-state';

/** Hauteur au-dessus du sol (m). */
const ABOVE = 1.4;
/** Couleur de sélection du HUD (--hx-accent), un peu plus claire en 3D. */
const ACCENT = 0x4cc3ff;
const SHADOW = 0x0b1822;

type Ground = { height(x: number, z: number): number };

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

export class PisteHighlight {
  readonly group = new THREE.Group();
  private readonly fill: THREE.Mesh;
  private readonly rimShadow: THREE.Mesh;
  private readonly rim: THREE.Mesh;
  private readonly arrows: THREE.Mesh;
  private key = '';

  constructor(
    private readonly cam: RtsCamera,
    private readonly store: ClientStore,
    private readonly ground: Ground,
  ) {
    this.group.name = 'piste-selectionnee';
    this.fill = this.layer(0xffffff, 0.16, 6);
    this.rimShadow = this.layer(SHADOW, 0.38, 7);
    this.rim = this.layer(ACCENT, 0.95, 8);
    this.arrows = this.layer(ACCENT, 0.9, 9);
    this.group.visible = false;
  }

  private layer(color: number, opacity: number, order: number): THREE.Mesh {
    const mat = new THREE.MeshBasicNodeMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide });
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -3;
    mat.polygonOffsetUnits = -3;
    const m = new THREE.Mesh(new THREE.BufferGeometry(), mat);
    m.renderOrder = order;
    m.frustumCulled = false;
    this.group.add(m);
    return m;
  }

  /** À chaque image : suit la sélection, le tracé de la piste et la distance de la caméra. */
  frame(): void {
    const sel = ui.selection.value;
    const r = this.store.replica;
    const p = sel?.kind === 'piste' && r ? r.pistes.get(sel.id) : undefined;
    if (!p || p.path.length < 2 || ui.pisteEdit.value?.id === p.id) {
      if (this.group.visible) {
        this.group.visible = false;
        this.key = '';
      }
      return;
    }
    const dist = this.cam.distance;
    // Paliers de distance : on ne refait la géométrie que si les tailles changent nettement.
    const bucket = Math.round(Math.log2(Math.max(40, dist)) * 4);
    const mid = p.path[Math.floor(p.path.length / 2)] as Vec2;
    const key = `${p.id}:${p.width}:${Math.round(p.length)}:${p.path.length}:${mid[0]},${mid[1]}:${bucket}`;
    if (key === this.key) return;
    this.key = key;
    this.build(p.path, p.width, dist);
    this.group.visible = true;
  }

  private build(path: Vec2[], width: number, dist: number): void {
    const pts = resample(path, 5);
    const n = pts.length;
    // Tangentes lissées sur deux points de part et d'autre : bords réguliers dans les lacets.
    const tx = new Float32Array(n);
    const tz = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const a = pts[Math.max(0, i - 2)] as Vec2;
      const b = pts[Math.min(n - 1, i + 2)] as Vec2;
      const dx = b[0] - a[0];
      const dz = b[1] - a[1];
      const l = Math.hypot(dx, dz) || 1;
      tx[i] = dx / l;
      tz[i] = dz / l;
    }
    const half = width / 2;
    const e = clamp(dist * 0.0032, 1.2, 7);
    this.setGeometry(this.fill, this.strips(pts, tx, tz, [[-half, half]]));
    this.setGeometry(this.rimShadow, this.strips(pts, tx, tz, [[half - e, half + e * 1.5], [-half - e * 1.5, -half + e]]));
    this.setGeometry(this.rim, this.strips(pts, tx, tz, [[half - e * 0.5, half + e * 0.5], [-half - e * 0.5, -half + e * 0.5]]));
    this.setGeometry(this.arrows, this.chevrons(pts, tx, tz, width, dist));
  }

  private setGeometry(m: THREE.Mesh, g: THREE.BufferGeometry): void {
    m.geometry.dispose();
    m.geometry = g;
  }

  /** Bandes parallèles à l'axe, entre deux décalages latéraux (m), posées sur le relief. */
  private strips(pts: Vec2[], tx: Float32Array, tz: Float32Array, bands: [number, number][]): THREE.BufferGeometry {
    const n = pts.length;
    const pos: number[] = [];
    const idx: number[] = [];
    const h = this.ground;
    for (const [o0, o1] of bands) {
      const base = pos.length / 3;
      for (let i = 0; i < n; i++) {
        const [x, z] = pts[i] as Vec2;
        // Normale : droite de la direction de descente.
        const nx = tz[i] as number;
        const nz = -(tx[i] as number);
        const ax = x + nx * o0;
        const az = z + nz * o0;
        const bx = x + nx * o1;
        const bz = z + nz * o1;
        pos.push(ax, h.height(ax, az) + ABOVE, az, bx, h.height(bx, bz) + ABOVE, bz);
        if (i < n - 1) {
          const k = base + i * 2;
          idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setIndex(idx);
    return g;
  }

  /** Chevrons à plat sur l'axe, pointés vers l'aval, régulièrement espacés. */
  private chevrons(pts: Vec2[], tx: Float32Array, tz: Float32Array, width: number, dist: number): THREE.BufferGeometry {
    const size = Math.min(width * 0.6, clamp(dist * 0.014, 6, 22));
    const gap = Math.max(40, size * 5);
    const pos: number[] = [];
    const h = this.ground;
    const put = (x: number, z: number) => pos.push(x, h.height(x, z) + ABOVE + 0.1, z);
    let acc = 0;
    let next = gap / 2;
    for (let i = 1; i < pts.length - 1; i++) {
      const a = pts[i - 1] as Vec2;
      const b = pts[i] as Vec2;
      acc += Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (acc < next) continue;
      next += gap;
      const [x, z] = b;
      const fx = tx[i] as number;
      const fz = tz[i] as number;
      const nx = fz;
      const nz = -fx;
      // Pointe vers l'aval, deux ailes, encoche au dos.
      const tipX = x + fx * size * 0.55;
      const tipZ = z + fz * size * 0.55;
      const notchX = x - fx * size * 0.12;
      const notchZ = z - fz * size * 0.12;
      const backX = x - fx * size * 0.45;
      const backZ = z - fz * size * 0.45;
      put(tipX, tipZ);
      put(backX + nx * size * 0.5, backZ + nz * size * 0.5);
      put(notchX, notchZ);
      put(tipX, tipZ);
      put(notchX, notchZ);
      put(backX - nx * size * 0.5, backZ - nz * size * 0.5);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    return g;
  }
}
